import { Context, ConversationSource } from '@/shared/types/chat';
import { LanguageModelOverride } from '@/shared/types/tryIt';

import { ConversationTurn } from './conversation';

/**
 * The modal as a state machine.
 *
 * `mode` is the machine state. Each kind carries a `phase` that captures
 * sub-state (loading / translating / sending / etc.). Transitions are explicit
 * — the only way to change phase is via a state-machine reducer
 * (`sendStarted`, `sendSucceeded`, `replayContentLoaded`, …) — never by
 * setting flags in isolation.
 *
 * ```
 * closed
 *   ├── openTest ───→ test:idle
 *   │       ├── sendStarted ────→ test:sending
 *   │       │       ├── sendSucceeded ─→ test:idle (+ new turn, requestId, context)
 *   │       │       └── sendFailed    ─→ test:error
 *   │       └── reset ────→ test:idle (new session)
 *   └── openReplay ────→ replay:loading
 *           ├── replayContentLoaded ─→ replay:ready (+ turns)
 *           ├── replayLanguageChanged(language) ─→ replay:translating
 *           ├── replayTranslated(turns)      ─→ replay:ready (+ translated turns)
 *           └── replayFailed                 ─→ replay:error
 * ```
 */

export type TestMode = {
  kind: 'test';
  tryItSessionId: string;
  brainId: string;
  hasMessages: boolean;
  phase: 'idle' | 'sending';
};

export type ReplayMode = {
  kind: 'replay';
  sessionId: string;
  sessionStart?: string;
  language?: string;
  // Brain that handled the original session. Resolved from the analytics
  // payload (`content.brain_parent_id`) on `replayContentLoaded` and undefined
  // until then.
  brainId?: string;
  phase: 'loading' | 'translating' | 'ready' | 'error';
};

export type TryItMode = TestMode | ReplayMode;

export enum TabId {
  Details = 'details',
  Context = 'context',
  Conversation = 'conversation',
  User = 'user',
  Review = 'review',
  Authentication = 'authentication',
  Model = 'model',
}

export type TryItSelection = {
  /** Index of the selected turn in `messages` — the stable, unique turn key. */
  turnIndex: number | null;
  actionId: string | null;
  requisiteIdx: number | null;
  ifNoRequisite: boolean | null;
};

/**
 * Fields common to both modes. Mode-specific data lives on the `mode` member
 * itself (a discriminated union). `messages` is the unified canonical timeline
 * — both modes write the same `ConversationTurn[]` here. For replay,
 * `useReplayMessagesSync` parses the Apollo payload and dispatches; for test,
 * `useTestSend` appends per response.
 */
export type TryItState = {
  mode: TryItMode | null;
  messages: ConversationTurn[];
  selection: TryItSelection | null;
  context: Context[];
  lastRequestId: string | null;
  ui: { activeTab: TabId };
  /** Where the conversation came from. Drives how responses render. */
  source: ConversationSource | null;
  /** True once any turn in this conversation has been corrected. */
  isAmended: boolean;
  /** Explicit model override applied to live-test sends. */
  languageModelOverride: LanguageModelOverride | null;
};
